<script setup>
import {computed, onMounted, onUnmounted, nextTick} from 'vue';
import 'jquery-ui';
import {renderWithPoi} from '../utility/addressHelper';
// v-model from parent binds to the model state object
const model = defineModel({
  type: Object,
  default: () => ({})
});

const fullName = computed({
  get: () => `${model.value.firstName || ''} ${model.value.lastName || ''}`.trim(),
  set: (val) => {
    const parts = String(val || '').split(' ');
    model.value.firstName = parts.shift() || '';
    model.value.lastName = parts.join(' ') || '';
  }
});

// Address autocomplete for driver onboarding
let driverAddressInput = null;
let driverAddressAutocompleteInitialized = false;

// Parse Google Maps address_components
const parseAddressComponents = (addressComponents) => {
  const parsed = {
    city: '',
    state: '',
    country: '',
    postalCode: ''
  };

  if (!addressComponents || !Array.isArray(addressComponents)) {
    return parsed;
  }

  addressComponents.forEach((component) => {
    const types = component.types || [];
    
    // City - check multiple possible types
    if (types.includes('locality')) {
      parsed.city = component.long_name;
    } else if (!parsed.city && types.includes('postal_town')) {
      parsed.city = component.long_name;
    } else if (!parsed.city && types.includes('sublocality_level_1')) {
      parsed.city = component.long_name;
    }
    
    // State/Province
    if (types.includes('administrative_area_level_1')) {
      parsed.state = component.short_name; // Use short_name for abbreviations (e.g., "CA", "NY")
    }
    
    // Country
    if (types.includes('country')) {
      parsed.country = component.long_name;
    }
    
    // Postal Code
    if (types.includes('postal_code')) {
      parsed.postalCode = component.long_name;
    }
  });

  return parsed;
};

const initDriverAddressAutocomplete = () => {
  if (!driverAddressInput || driverAddressAutocompleteInitialized) return;
  try {
    $(driverAddressInput).autocomplete({
      source: (request, response) => {
        fetch(`/api/dispatch-ride/autocomplete?query=${encodeURIComponent(request.term)}`)
            .then((res) => res.ok ? res.json() : Promise.reject(res.status))
            .then((data) => {
              const suggestions = (data?.results || []).map((result) => ({
                label: renderWithPoi(result),
                value: result.name,
                placeId: result.id,
                result,
              }));
              response(suggestions);
            })
            .catch(() => response([]));
      },
      minLength: 3,
    }).on('autocompleteselect', (event, ui) => {
      const selectedItem = ui.item;
      model.value.address = selectedItem?.value || '';
      
      // Fetch full address details using place_id
      if (selectedItem?.placeId) {
        fetch(`/api/dispatch-ride/geocode-by-place-id?placeId=${encodeURIComponent(selectedItem.placeId)}`)
          .then((res) => res.ok ? res.json() : Promise.reject(res.status))
          .then((data) => {
            if (data?.results && data.results.length > 0) {
              const addressComponents = data.results[0].address_components;
              const parsed = parseAddressComponents(addressComponents);
              
              // Update model with parsed address components
              if (parsed.city) model.value.city = parsed.city;
              if (parsed.state) model.value.state = parsed.state;
              if (parsed.country) model.value.country = parsed.country;
              if (parsed.postalCode) model.value.postalCode = parsed.postalCode;
            }
          })
          .catch((err) => {
            console.error('Failed to fetch address details:', err);
          });
      }
    }).autocomplete('instance')._renderItem = (ul, item) => {
      return $('<li>').append(item.label).appendTo(ul);
    };
    driverAddressAutocompleteInitialized = true;
  } catch (e) {
    console.error('Failed to init driver address autocomplete', e);
  }
};

onMounted(async () => {
  await nextTick();
  initDriverAddressAutocomplete();
});

onUnmounted(() => {
  try {
    if (driverAddressInput) {
      $(driverAddressInput).autocomplete('destroy');
    }
  } catch (e) {
    // ignore
  }
});
</script>

<template>
  <div class="driver-onboarding-step driver-onboarding-step-personal">
    <div class="row g-3">
      <div class="row">
        <div class="col-md-6">
          <label class="form-label f-w-600" for="driver-email-input">Email</label>
          <input id="driver-email-input" class="form-control"
                 type="email" v-model="model.email" required readonly disabled>
          <small class="text-muted">Email cannot be changed here. To update your email, please speak with your admin.</small>
        </div>
      </div>
      <div class="col-md-6">
        <label class="form-label f-w-600" for="driver-first-name-input">First Name</label>
        <input id="driver-first-name-input" class="form-control" type="text" v-model="model.firstName"
               placeholder="e.g., Jane" required>
        <div id="driver-first-name-error" class="invalid-feedback d-none">Please enter your first name.</div>
      </div>
      <div class="col-md-6">
        <label class="form-label f-w-600" for="driver-last-name-input">Last Name</label>
        <input id="driver-last-name-input" class="form-control" type="text" v-model="model.lastName"
               placeholder="e.g., Doe" required>
        <div id="driver-last-name-error" class="invalid-feedback d-none">Please enter your last name.</div>
      </div>

      <div class="col-md-6">
        <label class="form-label f-w-600" for="driver-dob-input">Date of Birth</label>
        <input id="driver-dob-input" class="form-control" type="date" v-model="model.dateOfBirth" required>
        <small class="text-muted">You must be at least 18 years old to drive.</small>
        <div id="driver-dob-error" class="invalid-feedback d-none">Please provide a valid date of birth. You must be at least 18 years old.</div>
      </div>

      <div class="col-md-6">
        <label class="form-label f-w-600" for="driver-phone-number-input">Phone Number</label>
        <div class="input-group">
          <select class="form-select" v-model="model.countryCode" style="max-width: 120px;">
            <option value="+1">+1 (US/CA)</option>
            <option value="+44">+44 (UK)</option>
            <option value="+234">+234 (NG)</option>
            <option value="+91">+91 (IN)</option>
            <option value="+61">+61 (AU)</option>
          </select>
          <input id="driver-phone-number-input" class="form-control" type="tel" v-model="model.phoneNumber"
                 placeholder="5551234567" required>
        </div>
        <small class="text-muted">Select country code and enter the rest of your phone number.</small>
        <div id="driver-phone-number-error" class="invalid-feedback d-none">Please enter a valid phone number.</div>
      </div>

      <div class="col-12">
        <label class="form-label f-w-600" for="driver-address-input">Street Address</label>
        <input id="driver-address-input" class="form-control" type="text" v-model="model.address"
               placeholder="123 Main Street" required ref="driverAddressInput">
        <div id="driver-address-error" class="invalid-feedback d-none">Please enter your street address.</div>
      </div>

      <div class="col-md-6">
        <label class="form-label f-w-600" for="driver-city-input">City</label>
        <input id="driver-city-input" class="form-control" type="text" v-model="model.city"
               placeholder="e.g., San Francisco" required>
        <div id="driver-city-error" class="invalid-feedback d-none">Please enter your city.</div>
      </div>

      <div class="col-md-6">
        <label class="form-label f-w-600" for="driver-state-input">State/Province</label>
        <input id="driver-state-input" class="form-control" type="text" v-model="model.state"
               placeholder="e.g., CA" required>
        <div id="driver-state-error" class="invalid-feedback d-none">Please enter your state.</div>
      </div>

      <div class="col-md-6">
        <label class="form-label f-w-600" for="driver-country-input">Country</label>
        <input id="driver-country-input" class="form-control" type="text" v-model="model.country"
               placeholder="e.g., United States" required>
        <div id="driver-country-error" class="invalid-feedback d-none">Please enter your country.</div>
      </div>

      <div class="col-md-6">
        <label class="form-label f-w-600" for="driver-postal-code-input">Postal Code</label>
        <input id="driver-postal-code-input" class="form-control" type="text" v-model="model.postalCode"
               placeholder="e.g., 94102" required>
        <div id="driver-postal-code-error" class="invalid-feedback d-none">Please enter your postal code.</div>
      </div>
    </div>
  </div>
</template>

<style scoped>
</style>
